import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CircularProgress from "@mui/material/CircularProgress";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import Divider from "@mui/material/Divider";

export type DecomposeWorkflowStep = "assign" | "crop" | "export" | "complete" | "error";

export type DecomposeSpeakerView = {
    key: string;
    speakerName: string;
    assigned: boolean;
    color?: string;
    cropCaptured?: boolean;
};

export type DecomposeExportRow = {
    key: string;
    speakerName: string;
    status: "pending" | "exporting" | "uploading" | "complete" | "error";
    progressPct?: number;
    message?: string;
};

type DecomposeClipSidebarProps = {
    onBack: () => void;
    step: DecomposeWorkflowStep;
    hasVideo: boolean;
    speakers: DecomposeSpeakerView[];
    currentCropSpeakerIndex?: number;
    isCropModeActive?: boolean;
    exports?: DecomposeExportRow[];
    error?: string | null;
    onValidateAssignments?: () => void;
    onStartCrop?: () => void;
};

const statusTone = (status: DecomposeExportRow["status"]): string => {
    if (status === "complete") return "#166534";
    if (status === "error") return "#B91C1C";
    if (status === "uploading") return "#1D4ED8";
    if (status === "exporting") return "#7C3AED";
    return "#6B7280";
};

const statusLabel = (status: DecomposeExportRow["status"]): string => {
    if (status === "complete") return "Done";
    if (status === "error") return "Failed";
    if (status === "uploading") return "Uploading";
    if (status === "exporting") return "Processing";
    return "Queued";
};

const DecomposeClipSidebar: React.FC<DecomposeClipSidebarProps> = ({
    onBack,
    step,
    hasVideo,
    speakers,
    currentCropSpeakerIndex,
    isCropModeActive,
    exports,
    error,
    onValidateAssignments,
    onStartCrop,
}) => {
    const unassignedCount = speakers.filter((s) => !s.assigned).length;
    const currentSpeaker =
        Number.isFinite(currentCropSpeakerIndex) && currentCropSpeakerIndex != null
            ? speakers[currentCropSpeakerIndex]
            : undefined;

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                p: "3px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "8px" }}>
                <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                    <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
                    Separate speakers
                </Typography>
            </Box>

            <Box
                data-tour="decompose-speakers-panel"
                sx={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                }}
            >
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Split this clip into separate speaker tracks (2-4 assigned speakers required).
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
                    {hasVideo
                        ? "Assign speakers then set crop for each speaker. Separation will start automatically."
                        : "Assign speakers. Separation will start automatically."}
                </Typography>
            </Box>

            <Box
                sx={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    flex: 1,
                    minHeight: 0,
                }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                    Speakers
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {speakers.map((speaker, idx) => {
                        const cropDone = !hasVideo || Boolean(speaker.cropCaptured);
                        const isCropTarget = step === "crop" && idx === currentCropSpeakerIndex;
                        return (
                            <Box
                                key={speaker.key}
                                sx={{
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "10px",
                                    px: "10px",
                                    py: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                {speaker.assigned ? (
                                    <CheckRoundedIcon sx={{ fontSize: 16, color: "#166534" }} />
                                ) : (
                                    <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: "#111827",
                                            fontWeight: 500,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {speaker.speakerName}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                                        {speaker.assigned ? "Assigned" : "Needs assignment"}
                                        {hasVideo ? ` · ${cropDone ? "Crop ready" : "Crop pending"}` : ""}
                                        {isCropTarget ? " · Select crop now" : ""}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "999px",
                                        backgroundColor: speaker.color || "#9CA3AF",
                                        border: "1px solid #D1D5DB",
                                        flexShrink: 0,
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>

                <Divider sx={{ borderColor: "#E5E7EB", my: "2px" }} />

                {step === "assign" ? (
                    <>
                        <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
                            {unassignedCount > 0
                                ? "Name each New Speaker tag in the transcript, then continue."
                                : "All speaker tags are assigned."}
                        </Typography>
                        <Button
                            data-tour="decompose-continue-button"
                            fullWidth
                            onClick={onValidateAssignments}
                            disabled={!onValidateAssignments}
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "#FFFFFF",
                                backgroundColor: "#1A1A1A",
                                borderRadius: "8px",
                                height: "40px",
                                "&:hover": { backgroundColor: "#2B2B2B" },
                                "&:disabled": { backgroundColor: "#E5E7EB", color: "#9CA3AF" },
                            }}
                        >
                            Continue
                        </Button>
                    </>
                ) : null}

                {step === "crop" ? (
                    <>
                        <Box
                            data-tour="decompose-crop-instructions"
                            sx={{
                                border: "1px solid #D1D5DB",
                                backgroundColor: "#F9FAFB",
                                borderRadius: "10px",
                                px: "10px",
                                py: "8px",
                            }}
                        >
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                Crop target
                            </Typography>
                            {currentSpeaker ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "2px" }}>
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "999px",
                                            backgroundColor: currentSpeaker.color || "#6B7280",
                                            border: "1px solid #D1D5DB",
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                                        {currentSpeaker.speakerName}
                                    </Typography>
                                </Box>
                            ) : null}
                            <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "#4B5563", mt: "4px" }}>
                                Set the crop in canvas mode, then click Done.
                            </Typography>
                        </Box>
                        <Button
                            fullWidth
                            onClick={onStartCrop}
                            disabled={!onStartCrop || Boolean(isCropModeActive)}
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "#FFFFFF",
                                backgroundColor: "#1A1A1A",
                                borderRadius: "8px",
                                height: "40px",
                                "&:hover": { backgroundColor: "#2B2B2B" },
                                "&:disabled": { backgroundColor: "#E5E7EB", color: "#9CA3AF" },
                            }}
                        >
                            {isCropModeActive ? "Cropping in canvas..." : "Start crop"}
                        </Button>
                    </>
                ) : null}

                {step === "export" || step === "complete" || step === "error" ? (
                    <Box data-tour="decompose-export-list" sx={{ display: "flex", flexDirection: "column", gap: "8px", overflow: "auto" }}>
                        {(exports ?? []).map((row) => (
                            <Box
                                key={row.key}
                                data-decompose-export-status={row.status}
                                sx={{
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "10px",
                                    px: "10px",
                                    py: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                {row.status === "exporting" || row.status === "uploading" ? (
                                    <CircularProgress size={14} sx={{ color: statusTone(row.status) }} />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: "999px",
                                            border: "1px solid #D1D5DB",
                                            backgroundColor: row.status === "complete" ? "#DCFCE7" : "#F3F4F6",
                                        }}
                                    />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>
                                        {row.speakerName}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: statusTone(row.status) }}>
                                        {statusLabel(row.status)}
                                        {Number.isFinite(row.progressPct) ? ` · ${Math.round(Number(row.progressPct))}%` : ""}
                                    </Typography>
                                    {row.message ? (
                                        <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                                            {row.message}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : null}

                {!!error ? (
                    <Box
                        sx={{
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            borderRadius: "10px",
                            px: "10px",
                            py: "8px",
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "#991B1B" }}>{error}</Typography>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};

export default DecomposeClipSidebar;
